import { RefObject, useCallback, useMemo } from 'react';
import type { TableComponent } from '@ohpen/enso-ui';

import { AssistantListType, AssistantTableProps, useAssistantPageContext } from './useAssistantPageContext';
import { ListTableChange, ListUrlStateProps, useListUrlState } from './useListUrlState';
import type { ListColumnFilters } from '@pmp/utils';

export type ListTableStateProps<T> = AssistantTableProps<T> & ListUrlStateProps;

/**
 * The shared table wiring every list wants: it reports itself to the assistant and keeps its search
 * and column filters in the URL. One object to spread, so the two `onGlobalSearch` handlers cannot
 * shadow each other at the call site.
 *
 * Pass the list's `columns` when they can change after mount, so a URL filter on a column that is not
 * there yet is applied once it appears.
 */
export const useListTableState = <T,>(
  entityType: AssistantListType | undefined,
  rowKey: string,
  tableRef: RefObject<TableComponent | null>,
  columns?: readonly unknown[],
): ListTableStateProps<T> => {
  // Read when the turn is built rather than reported on each change: the count moves with every filter,
  // page and data load, and what the agent needs is the one the pager is showing at that moment.
  const readRowCounts = useCallback(() => tableRef.current?.getState().rows, [tableRef]);

  const assistant = useAssistantPageContext<T>(entityType, rowKey, readRowCounts);
  const urlState = useListUrlState(entityType, tableRef, columns);

  const onGlobalSearch = useCallback(
    (searchText: string) => {
      assistant.onGlobalSearch?.(searchText);
      urlState.onGlobalSearch?.(searchText);
    },
    [assistant, urlState],
  );

  const onFiltersChange = useCallback(
    (filters: ListColumnFilters) => {
      assistant.onFiltersChange?.(filters);
      urlState.onFiltersChange?.(filters);
    },
    [assistant, urlState],
  );

  // Where a filter picked in a column header lands: the grid reports it through AntD's change callback,
  // not through `onFiltersChange`, so both readers would otherwise never hear about it.
  const onChange = useCallback<ListTableChange>(
    (pagination, filters, ...rest) => {
      urlState.onChange?.(pagination, filters, ...rest);
      assistant.onFiltersChange?.(filters);
    },
    [assistant, urlState],
  );

  // The Reset Filters button clears the grid without `onChange`, so both readers take the applied
  // snapshot off the ref here rather than each holding its own copy of that quirk.
  const setActiveFilters = useCallback(
    (activeFilters: boolean) => {
      urlState.setActiveFilters?.(activeFilters);
      const applied = tableRef.current?.getState().filters.byColumn;
      if (applied !== undefined) assistant.onFiltersChange?.(applied);
    },
    [assistant, tableRef, urlState],
  );

  return useMemo(
    () => ({ ...assistant, ...urlState, onGlobalSearch, onFiltersChange, onChange, setActiveFilters }),
    [assistant, onChange, onFiltersChange, onGlobalSearch, setActiveFilters, urlState],
  );
};
